import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  process.env?.REACT_APP_API_BASE_URL ||
  "http://localhost:8000/api";
const STORAGE_BASE = API_BASE.replace("/api", "") + "/storage/";

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // On refresh (no transient flag) or empty query → go home
  useEffect(() => {
    if (!query.trim() || !window.__searched) {
      navigate("/", { replace: true });
    }
  }, [query, navigate]);

  // Fetch all products once
  useEffect(() => {
    if (!query.trim()) return;
    setLoading(true);
    axios
      .get(`${API_BASE}/productlist`)
      .then((res) => {
        setAllProducts(res.data?.data || []);
      })
      .catch(() => {
        setAllProducts([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Client-side filter
  const results = useMemo(() => {
    if (!query.trim()) return [];
    const lower = query.toLowerCase();
    return allProducts.filter(
      (p) =>
        p.name?.toLowerCase().includes(lower) ||
        p.category?.name?.toLowerCase() === lower,
    );
  }, [allProducts, query]);

  const getImage = (product) => {
    const img = product.primary_image || product.images?.[0];
    if (!img) return "/img/product/placeholder.jpg";
    const src = img.image || img;
    return src.startsWith("http") ? src : STORAGE_BASE + src;
  };

  return (
    <main className="bg-white min-h-screen font-sans antialiased text-gray-700">
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Heading */}
        <div className="mb-8">
          <p className="text-sm italic text-[#43a047] font-serif tracking-wide mb-1">
            Search Results
          </p>
          <h2 className="text-2xl md:text-3xl font-medium text-[#112244] tracking-wide">
            {loading
              ? "Searching..."
              : `${results.length} result${results.length !== 1 ? "s" : ""} for "${query}"`}
          </h2>
        </div>

        {/* Loading spinner */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <span className="w-12 h-12 border-4 border-gray-200 border-t-[#43a047] rounded-full animate-spin inline-block" />
          </div>
        )}

        {/* No results */}
        {!loading && results.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-4">
              No products found for &ldquo;{query}&rdquo;
            </p>
            <Link
              to="/"
              className="inline-block btn-readmore px-8 py-3 text-xs font-bold uppercase tracking-wider rounded-full transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}

        {/* Results grid */}
        {!loading && results.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {results.map((product) => (
              <Link
                key={product.product_id}
                to={`/product-details/${product.product_id}`}
                className="group border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all p-4 bg-white text-center block"
              >
                {/* Image */}
                <div className="h-40 w-full flex items-center justify-center overflow-hidden mb-4">
                  <img
                    src={getImage(product)}
                    alt={product.name}
                    className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = "/img/product/placeholder.jpg";
                    }}
                  />
                </div>

                {/* Category */}
                {product.category?.name && (
                  <p className="text-[10px] text-[#43a047] uppercase tracking-wider mb-1">
                    {product.category.name}
                  </p>
                )}

                {/* Name */}
                <h4 className="text-gray-600 font-normal text-xs mb-2 line-clamp-2 px-1">
                  {product.name}
                </h4>

                {/* Badges */}
                <div className="flex justify-center gap-1 flex-wrap">
                  {product.is_new_arrival && (
                    <span className="bg-[#43a047] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wider">
                      NEW
                    </span>
                  )}
                  {product.is_best_seller && (
                    <span className="bg-[#112244] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wider">
                      BEST SELLER
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
